import { Component, Inject, Input, forwardRef, OnInit } from '@angular/core';
import { NgAisInstantSearch } from 'angular-instantsearch';
import { CatalogWidgetDirective, IBaseItem } from '@app/shared/components/catalog/catalog-widgets/catalog-widget';
import { connectCurrentRefinedValues } from 'instantsearch.js/es/connectors';
import {
  FiltersService,
  PriceRangeState,
  formatLabel,
  ToggleFacets
} from '@app/shared/components/catalog/services/filters.service';
import { LocaleService } from '@app/core/i18n';
import { CatalogFilter } from '@app/shared/model/interfaces/catalog';
import { RefinementsService } from '@app/shared/components/catalog/services/refinements.service';
import { I18nData } from '@app/core/i18n/services';
import { ContentsMarketingFilter, Deal } from '@vestiaire/api-sdk-angular';

interface ICurrentRefinementItem extends IBaseItem {
  attributeName: string;
  widgetType?: string;
  name?: string;
  computedLabel?: string;
  type?: string;
  sameLabelRef?: Set<any> | number[];
  shouldHide?: boolean;
}

interface IAttribute {
  name: string;
  label: string;
}

@Component({
  selector: 'vc-catalog-widget-current-refinements',
  templateUrl: './catalog-widget-current-refinements.component.html',
  styleUrls: ['./catalog-widget-current-refinements.component.scss']
})
export class CatalogWidgetCurrentRefinementsComponent extends CatalogWidgetDirective implements OnInit {
  @Input()
  public attributes: IAttribute[];

  @Input()
  marketingFilterLocal: ContentsMarketingFilter;

  @Input()
  currentDeal: Deal;

  state: {
    attributes: {};
    clearAllClick: () => void;
    clearAllUrl: () => void;
    refine: (value: string | ICurrentRefinementItem) => void;
    createURL: () => string;
    refinements: ICurrentRefinementItem[];
    editorRefinements: any;
    widgetParams: {};
  };

  constructor(
    @Inject(forwardRef(() => NgAisInstantSearch)) public instantSearchParent,
    private filtersService: FiltersService,
    private localeService: LocaleService,
    private refinementsService: RefinementsService,
    public i18nData: I18nData
  ) {
    super('CatalogWidgetCurrentRefinementsComponent');
  }

  /**
   * refinements getter
   * Responsible for fetching and formating labels used for internal search
   * @readonly
   * @memberof CatalogWidgetCurrentRefinementsComponent
   */
  get refinements() {
    const transformedItems = [];
    for (const item of this.state.refinements || []) {
      transformedItems.push(this.formatRefinementItem(item));
    }
    return [...transformedItems, ...(this.editorRefinements || [])].filter(r => r);
  }

  get editorRefinements() {
    // so sorry
    return this.state.editorRefinements;
  }

  public ngOnInit() {
    this.createWidget(connectCurrentRefinedValues, {
      attributes: this.attributes,
      onlyListedAttributes: true
    });

    // Refinements subscription
    this.getRefinements();
    this.getEditorRefinements();

    super.ngOnInit();
  }

  private getEditorRefinements(): void {
    this.state.editorRefinements = this.refinementsService.getEditorRefinements();
    this.refinementsService.getEditorRefinements().subscribe(
      res => (this.state.editorRefinements = [res].filter(r => typeof r !== 'undefined')),
      err => {}
    );
  }

  /**
   * getRefinements subscribes to the refinements service and
   * injects current refinements into the state
   * @private
   * @memberof CatalogWidgetCurrentRefinementsComponent
   */
  private getRefinements(): void {
    // We subscribe to the refinements service in order to get
    // the current refinements applied to the page.
    this.refinementsService.getRefinements().subscribe(
      res => {
        const temporaryRefinements = [];
        for (const key in res) {
          if (!res.hasOwnProperty(key)) {
            continue;
          }

          const nameAndLabel = res[`${key}`][0];
          if (nameAndLabel) {
            const item = {
              computedLabel: nameAndLabel,
              attributeName: key,
              name: nameAndLabel,
              staticField: res[`${key}`][1] === 'staticField',
              type: 'disjunctive' // Always disjunctive
            };
            temporaryRefinements.push(this.formatRefinementItem(item));
          }
        }

        // Dedup items :
        // Because we merge items from the current state and the one available through the refinements
        // service we need to make sure that items don't overlap.
        const mergedRef: ICurrentRefinementItem[] = [...this.state.refinements, ...temporaryRefinements];
        const uniq: ICurrentRefinementItem[] = [];

        mergedRef.forEach((refItem, index) => {
          let isUniq = true;
          uniq.forEach((uniqItem, uniqIndex) => {
            if (uniqItem.computedLabel === refItem.computedLabel) {
              isUniq = false;
            }

            // In case we have same refinement names then we append a new
            // property on object with a reference to the item placement in
            // refinements array
            if (formatLabel(uniqItem) === formatLabel(refItem)) {
              uniqItem.sameLabelRef = uniqItem.sameLabelRef
                ? new Set([...Array.from(uniqItem.sameLabelRef), index, uniqIndex])
                : [index, uniqIndex];
              refItem.sameLabelRef = refItem.sameLabelRef
                ? new Set([...Array.from(refItem.sameLabelRef), index, uniqIndex])
                : [index, uniqIndex];
              refItem.shouldHide = true;
            }
          });
          if (!isUniq) {
            return;
          }
          uniq.push(refItem);
        });
        this.state.refinements = uniq;
      },
      err => {}
    );
  }

  private formatRefinementItem(item: any): ICurrentRefinementItem {
    return {
      ...item,
      ...{
        label: formatLabel(item),
        widgetType: ToggleFacets.indexOf(item.attributeName) > -1 ? 'toggle' : 'regular'
      }
    };
  }

  /**
   * Before clear refinment, do a recursive refine on all depending & refined facets
   * We also check if the item is of type staticField in which case we simply return as
   * this tag is linked to the current location
   * @param {*} item
   * @memberof CatalogWidgetCurrentRefinementsComponent
   */
  refine(item) {
    if (item && item.staticField) {
      return;
    }

    const filterList = this.filtersService.getDefaultFilters(this.localeService);
    const { sameLabelRef } = item;
    if (sameLabelRef && sameLabelRef.size > 0) {
      sameLabelRef.forEach(refIndex => {
        this.refineItem(filterList, this.state.refinements[refIndex]);
        this.refinePriceRange(this.state.refinements[refIndex]);
      });
      return;
    }

    this.refineItem(filterList, item);
    this.refinePriceRange(item);
  }

  /**
   * refinePriceRange is responsible for formating the
   * pricerange label and setting appropriate values into the
   * filtersService
   * @private
   * @param {*} item
   * @memberof CatalogWidgetCurrentRefinementsComponent
   */
  private refinePriceRange(item) {
    if (item.attributeName.indexOf('price.cents') !== -1 && !item.isRefined) {
      const priceRangeState = {} as PriceRangeState;
      if (item.operator === '>=') {
        priceRangeState.resetMin = true;
      }
      if (item.operator === '<=') {
        priceRangeState.resetMax = true;
      }
      this.filtersService.setPriceFilterStatut(priceRangeState);
    }
  }

  /**
   * Say if other refinments are depending on current item
   * @param filters
   * @param parentItem
   * @memberof CatalogWidgetCurrentRefinementsComponent
   */
  hasDependencies(filters: CatalogFilter[], parentItem: ICurrentRefinementItem): boolean {
    let result = false;
    filters
      .filter(filter => filter.requiredFacet && filter.requiredFacet === parentItem.attributeName)
      .forEach(filter => {
        result = this.refinements.find(refinment => filter.facet.indexOf(refinment.attributeName) !== -1);
      });

    return result;
  }

  /**
   * Do a recursive refinment of an item
   * @param filters
   * @param item
   * @memberof CatalogWidgetCurrentRefinementsComponent
   */

  refineItem(filters: CatalogFilter[], item: ICurrentRefinementItem) {
    filters
      .filter(filter => filter.requiredFacet && item.attributeName === filter.requiredFacet)
      .forEach(filter => {
        this.refinements
          .filter(refinment => filter.facet.indexOf(refinment.attributeName) >= 0)
          .forEach(refinment => {
            if (this.hasDependencies(filters, refinment)) {
              this.refineItem(filters, refinment);
            }
            this.state.refine(refinment);
          });
      });

    this.state.refine(item);
  }

  /**
   * clearAll is responsible for clearing all refinements items
   * @memberof CatalogWidgetCurrentRefinementsComponent
   */
  clearAll() {
    const priceRangeState = { resetMax: true, resetMin: true } as PriceRangeState;

    this.filtersService.setPriceFilterStatut(priceRangeState);
    this.state.clearAllClick();
  }

  /**
   * toggle clearn btn
   * @returns {boolean}
   * @memberof CatalogWidgetCurrentRefinementsComponent
   */
  toggleClearBtn(): boolean {
    return this.state.refinements.filter(r => (r as any).staticField === false || !('staticField' in r)).length > 0;
  }
}
